import React from "react";
import { View, Text, StyleSheet } from "@react-pdf/renderer";

const OutcomesTable = ({ data, prevData }) => {
  /**
   * data shape now:
   * [
   *   {
   *     outcomeTitle: "Well-being",
   *     questions: [
   *       {
   *         outcomeTitle: "Well-being",
   *         questionIndex: 0,
   *         questionText: "...",
   *         distribution: [...],
   *         n: 272,
   *         roles: [],
   *         overallN: 272,
   *         overallAverage: 7.29,
   *         overallStdev: ...
   *       }
   *     ]
   *   },
   *   {
   *     outcomeTitle: "Retention",
   *     questions: [
   *       {
   *         outcomeTitle: "Retention",
   *         questionIndex: 0,
   *         questionText: "...",
   *         distribution: [...],
   *         n: 272,
   *         roles: [],
   *         overallN: 272,
   *         overallAverage: 7.59,
   *         overallStdev: ...
   *       }
   *     ]
   *   }
   * ]
   *
   * We want to display a row for each question (or each outcome).
   * Below, we flatten them into an array of "row" objects that fit
   * the shape: { outcome, score, change }.
   */

  const calculateWeightedAverage = (questions) => {
    let totalWeightedScore = 0;
    let totalResponses = 0;

    questions.forEach((q) => {
      totalWeightedScore += q.overallAverage * q.n;
      totalResponses += q.n;
    });

    return totalResponses > 0 ? totalWeightedScore / totalResponses : 0;
  };

  // Group data by outcome and compute weighted averages
  const rows = data.map((outcomeItem, index) => {
    const { outcomeTitle, questions } = outcomeItem;

    // Get weighted average for this outcome
    const weightedAverage = calculateWeightedAverage(questions);
    const weightedScore = (weightedAverage * 10).toFixed(0); // Convert to percentage scale

    // Find corresponding outcome in prevData
    const prevOutcomeItem = prevData?.find(
      (prev) => prev.outcomeTitle === outcomeTitle
    );

    let change = "--";
    if (prevOutcomeItem) {
      const prevWeightedAvg = calculateWeightedAverage(
        prevOutcomeItem.questions
      );
      const changeValue = (weightedAverage - prevWeightedAvg) * 10;
      change = `${changeValue.toFixed(1)}%`;
    }

    return {
      outcome: outcomeTitle,
      score: weightedScore,
      change: change,
    };
  });

  return (
    <View style={styles.table}>
      {/* Table Header */}
      <View style={styles.tableHeader}>
        <View style={styles.elipse} />
        <Text style={[styles.headerCell, styles.factorColumn]}>
          Critical Outcome
        </Text>
        <Text style={[styles.headerCell, styles.scoreColumn]}>Score</Text>
        <Text style={[styles.headerCell, styles.changeColumn]}>Change</Text>
      </View>

      {/* Table Rows */}
      {rows.map((row, index) => (
        <View
          key={index}
          style={[
            styles.tableRow,
            index % 2 === 0 ? styles.oddRow : styles.evenRow,
            // If it's the last row in the table, add a bottom border radius
            index === rows.length - 1
              ? { borderBottomLeftRadius: 9, borderBottomRightRadius: 9 }
              : {},
          ]}
        >
          <Text style={[styles.cell, styles.factorColumn, { fontSize: 12 }]}>
            {row.outcome}
          </Text>
          <Text style={[styles.cell, styles.scoreColumn, { fontSize: 12 }]}>
            {row.score}%
          </Text>
          <Text
            style={[
              styles.cell,
              styles.changeColumn,
              row.change === "--"
                ? styles.noColorChange
                : row.change.includes("-")
                ? styles.negativeChange
                : row.change === "0%"
                ? styles.neutralChange
                : styles.positiveChange,
              { fontSize: 12 },
            ]}
          >
            {row.change}
          </Text>
        </View>
      ))}
    </View>
  );
};

// Styles for the Table
const styles = StyleSheet.create({
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#EBF1FD",
    borderRadius: 9,
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#fff",
    padding: 10,
    borderTopLeftRadius: 9,
    borderTopRightRadius: 9,
  },
  elipse: {
    width: 5,
    height: 5,
    borderRadius: 5,
    marginRight: 5,
    top: 3,
    backgroundColor: "#27CDA7",
  },
  headerCell: {
    fontSize: 10,
    fontFamily: "Raleway",
    fontWeight: 600,
    color: "#666D79",
  },
  factorColumn: {
    flex: 3,
  },
  scoreColumn: {
    flex: 1,
    textAlign: "center",
    marginRight: 3,
  },
  changeColumn: {
    flex: 1,
    textAlign: "center",
  },
  tableRow: {
    flexDirection: "row",
    padding: 10,
  },
  evenRow: {
    backgroundColor: "#FFFFFF",
  },
  oddRow: {
    backgroundColor: "#F3F8FF",
  },
  cell: {
    fontSize: 12,
    color: "#2A3039",
    fontFamily: "Raleway",
    fontWeight: 600,
  },
  positiveChange: {
    color: "#1D9A7D", // Secondary/600
  },
  negativeChange: {
    color: "#E34D59", // Example negative color
  },
  neutralChange: {
    color: "#F5A623", // Example neutral color
  },
  noColorChange: {
    color: "#2A3039",
  },
});

export default OutcomesTable;